import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import OutsourceSyncButton from "@/components/OutsourceSyncButton";
import { toast } from "sonner";
import {
  Printer,
  FileText,
  RefreshCw,
  Loader2,
  Download,
  Eye,
  RotateCw,
  Sun,
  Search,
  Plus,
  X,
  ListOrdered,
  Trash2,
  Save,
  Upload,
  RotateCcw,
  Undo2,
  PanelLeftClose,
  PanelLeft,
  Crop,
  ZoomIn,
  ZoomOut,
  Image as ImageIcon,
  History,
  Share2,
  Edit2,
  ExternalLink,
  CheckCheck,
  FileDown,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to check if image count matches product details
const checkImageCountMismatch = (imageCount, productDetails) => {
  if (!productDetails || productDetails === "-") return false;
  
  // Extract the first number from product details (e.g., "36" from "36_3x4_W")
  const match = productDetails.match(/^(\d+)/);
  if (!match) return false;
  
  const expectedCount = parseInt(match[1], 10);
  return imageCount !== expectedCount;
};

// Crop ratio presets
const CROP_RATIOS = [
  { name: "Free", value: null },
  { name: "Original", value: "original" },
  { name: "Square", value: 1 },
  { name: "9:16", value: 9/16 },
  { name: "16:9", value: 16/9 },
  { name: "4:5", value: 4/5 },
  { name: "5:4", value: 5/4 },
  { name: "3:4", value: 3/4 },
  { name: "4:3", value: 4/3 },
  { name: "2:3", value: 2/3 },
  { name: "3:2", value: 3/2 },
  { name: "5:7", value: 5/7 },
  { name: "7:5", value: 7/5 },
  { name: "1:2", value: 1/2 },
  { name: "2:1", value: 2/1 },
];

// Draggable Crop Box Component
function CropBox({ imageRef, cropArea, setCropArea, aspectRatio }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState(null); // 'move', 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
  const dragRef = useRef({ startX: 0, startY: 0, startCrop: null });

  const handleMouseDown = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragType(type);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startCrop: { ...cropArea }
    };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      if (!imageRef.current || !dragRef.current.startCrop) return;

      const rect = imageRef.current.getBoundingClientRect();
      const { startX, startY, startCrop } = dragRef.current;
      const deltaX = ((e.clientX - startX) / rect.width) * 100;
      const deltaY = ((e.clientY - startY) / rect.height) * 100;

      let newCrop = { ...startCrop };
      const minSize = 10;

      if (dragType === 'move') {
        newCrop.x = Math.max(0, Math.min(100 - startCrop.width, startCrop.x + deltaX));
        newCrop.y = Math.max(0, Math.min(100 - startCrop.height, startCrop.y + deltaY));
      } else {
        // Handle resize
        if (dragType.includes('w')) {
          const newX = Math.max(0, Math.min(startCrop.x + startCrop.width - minSize, startCrop.x + deltaX));
          newCrop.width = startCrop.width + (startCrop.x - newX);
          newCrop.x = newX;
        }
        if (dragType.includes('e')) {
          newCrop.width = Math.max(minSize, Math.min(100 - startCrop.x, startCrop.width + deltaX));
        }
        if (dragType.includes('n')) {
          const newY = Math.max(0, Math.min(startCrop.y + startCrop.height - minSize, startCrop.y + deltaY));
          newCrop.height = startCrop.height + (startCrop.y - newY);
          newCrop.y = newY;
        }
        if (dragType.includes('s')) {
          newCrop.height = Math.max(minSize, Math.min(100 - startCrop.y, startCrop.height + deltaY));
        }

        // Apply aspect ratio if set
        if (aspectRatio && aspectRatio !== "original") {
          const ratio = typeof aspectRatio === 'number' ? aspectRatio : parseFloat(aspectRatio);
          if (!isNaN(ratio)) {
            if (dragType.includes('e') || dragType.includes('w')) {
              newCrop.height = newCrop.width / ratio;
              if (newCrop.y + newCrop.height > 100) {
                newCrop.height = 100 - newCrop.y;
                newCrop.width = newCrop.height * ratio;
              }
            } else {
              newCrop.width = newCrop.height * ratio;
              if (newCrop.x + newCrop.width > 100) {
                newCrop.width = 100 - newCrop.x;
                newCrop.height = newCrop.width / ratio;
              }
            }
          }
        }
      }

      setCropArea(newCrop);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDragType(null);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragType, aspectRatio, imageRef, setCropArea]);

  const handleStyle = "absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-md";

  return (
    <>
      {/* Dark overlay outside crop area */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bg-black/50" style={{ top: 0, left: 0, right: 0, height: `${cropArea.y}%` }} />
        <div className="absolute bg-black/50" style={{ top: `${cropArea.y + cropArea.height}%`, left: 0, right: 0, bottom: 0 }} />
        <div className="absolute bg-black/50" style={{ top: `${cropArea.y}%`, left: 0, width: `${cropArea.x}%`, height: `${cropArea.height}%` }} />
        <div className="absolute bg-black/50" style={{ top: `${cropArea.y}%`, right: 0, width: `${100 - cropArea.x - cropArea.width}%`, height: `${cropArea.height}%` }} />
      </div>

      {/* Crop box */}
      <div
        className="absolute border-2 border-white cursor-move shadow-lg"
        style={{
          left: `${cropArea.x}%`,
          top: `${cropArea.y}%`,
          width: `${cropArea.width}%`,
          height: `${cropArea.height}%`,
        }}
        onMouseDown={(e) => handleMouseDown(e, 'move')}
      >
        {/* Grid lines - rule of thirds */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/50" />
          <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/50" />
          <div className="absolute top-1/3 left-0 right-0 h-px bg-white/50" />
          <div className="absolute top-2/3 left-0 right-0 h-px bg-white/50" />
        </div>

        {/* Corner handles - larger for easier grabbing */}
        <div className={`${handleStyle} -top-2 -left-2 cursor-nw-resize z-10`} onMouseDown={(e) => handleMouseDown(e, 'nw')} />
        <div className={`${handleStyle} -top-2 -right-2 cursor-ne-resize z-10`} onMouseDown={(e) => handleMouseDown(e, 'ne')} />
        <div className={`${handleStyle} -bottom-2 -left-2 cursor-sw-resize z-10`} onMouseDown={(e) => handleMouseDown(e, 'sw')} />
        <div className={`${handleStyle} -bottom-2 -right-2 cursor-se-resize z-10`} onMouseDown={(e) => handleMouseDown(e, 'se')} />

        {/* Edge handles */}
        <div className={`${handleStyle} -top-2 left-1/2 -translate-x-1/2 cursor-n-resize z-10`} onMouseDown={(e) => handleMouseDown(e, 'n')} />
        <div className={`${handleStyle} -bottom-2 left-1/2 -translate-x-1/2 cursor-s-resize z-10`} onMouseDown={(e) => handleMouseDown(e, 's')} />
        <div className={`${handleStyle} top-1/2 -left-2 -translate-y-1/2 cursor-w-resize z-10`} onMouseDown={(e) => handleMouseDown(e, 'w')} />
        <div className={`${handleStyle} top-1/2 -right-2 -translate-y-1/2 cursor-e-resize z-10`} onMouseDown={(e) => handleMouseDown(e, 'e')} />
        
        {/* Crop size indicator */}
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
          {Math.round(cropArea.width)}% × {Math.round(cropArea.height)}%
        </div>
      </div>
    </>
  );
}

export default function ProductionPage() {
  const [readyOrders, setReadyOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPanel, setShowPanel] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(100);
  
  const [selectedQueue, setSelectedQueue] = useState([]);
  const [queuePreviews, setQueuePreviews] = useState({});
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [allPreviewPages, setAllPreviewPages] = useState([]);
  const [imageEdits, setImageEdits] = useState({});
  const [editedIndices, setEditedIndices] = useState({});  // Track which images were edited per order
  const [refreshingEdits, setRefreshingEdits] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  
  // PDF History State
  const [pdfHistoryOpen, setPdfHistoryOpen] = useState(false);
  const [syncingLedger, setSyncingLedger] = useState(false);
  const [pdfList, setPdfList] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedPdfs, setSelectedPdfs] = useState([]);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingPdf, setRenamingPdf] = useState(null);
  const [newPdfName, setNewPdfName] = useState("");
  const [copiedLink, setCopiedLink] = useState(null);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  
  // PDF Naming State
  const [pdfFileName, setPdfFileName] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [showDraftNameInput, setShowDraftNameInput] = useState(false);
  const [autoSaveDraftId, setAutoSaveDraftId] = useState(() => localStorage.getItem("autoSaveDraftId") || null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(""); // "saving", "saved", "error"
  const isSavingRef = useRef(false);

  // Verify autoSaveDraftId is valid on mount
  useEffect(() => {
    const savedId = localStorage.getItem("autoSaveDraftId");
    if (savedId) {
      axios.get(`${API}/production/drafts`).then(resp => {
        const drafts = resp.data || [];
        const exists = drafts.find(d => d.id === savedId);
        if (!exists) {
          // Draft was deleted - clear localStorage
          setAutoSaveDraftId(null);
          localStorage.removeItem("autoSaveDraftId");
        }
      }).catch(() => {});
    }
  }, []);
  const [labelUploading, setLabelUploading] = useState(false);
  const [labelResults, setLabelResults] = useState(null); // {found: [], missing: []}
  const [courierCounts, setCourierCounts] = useState({}); // {Delhivery: 10, ...}
  const labelCourierMapRef = useRef({}); // Persists order->courier mapping

  const courierShortCodes = {
    'Delhivery':'DL','Xpressbees':'X','Shadowfax':'S','Valmo':'V',
    'ElasticRun':'ER','BlueDart':'BD','DTDC':'DTDC','Jusda':'J',
    'LoadShare':'LS','FedEx':'FX','Wow':'WOW','Shiprocket':'SR','SWA':'SWA'
  };
  const courierColors = {
    'Delhivery':'#dc2626','Xpressbees':'#ea580c','Shadowfax':'#7c3aed',
    'Valmo':'#2563eb','ElasticRun':'#16a34a','BlueDart':'#0369a1',
    'DTDC':'#b45309','Jusda':'#0891b2','LoadShare':'#be185d',
    'FedEx':'#4f46e5','Wow':'#0d9488','Shiprocket':'#dc2626','SWA':'#64748b'
  };
  const [labelSequence, setLabelSequence] = useState([]); // Original PDF sequence
  const labelInputRef = useRef(null);

  const handleLabelUpload = async (file) => {
    if (!file) return;
    setLabelUploading(true);
    setLabelResults(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await axios.post(`${API}/production/extract-label-ids`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const extractedIds = resp.data.order_ids || [];
      
      // Match with ready orders
      const found = [];
      const missing = [];
      
      // Add in sequence - clear queue first and add in PDF order
      const newQueue = [...selectedQueue]; // Keep existing selections
      for (const labelId of extractedIds) {
        const order = readyOrders.find(o => o.order_id && (o.order_id === labelId || o.order_id.includes(labelId)));
        if (order) {
          found.push({ labelId, orderId: order.id, order });
          // Add in sequence if not already in queue
          if (!newQueue.includes(order.id)) {
            newQueue.push(order.id);
          }
        } else {
          missing.push(labelId);
        }
      }
      // Calculate courier counts
      const orderCourierMap = resp.data.order_courier_map || {};
      labelCourierMapRef.current = orderCourierMap;
      const newCourierCounts = {};
      for (const { labelId } of found) {
        const courier = orderCourierMap[labelId] || "Unknown";
        if (!newCourierCounts[courier]) newCourierCounts[courier] = { ready: 0, notReady: 0 };
        newCourierCounts[courier].ready += 1;
      }
      for (const labelId of missing) {
        const courier = orderCourierMap[labelId] || "Unknown";
        if (!newCourierCounts[courier]) newCourierCounts[courier] = { ready: 0, notReady: 0 };
        newCourierCounts[courier].notReady += 1;
      }

      setSelectedQueue(newQueue);
      setLabelSequence(extractedIds);
      setCourierCounts(newCourierCounts);
      setLabelResults({ found, missing, total: extractedIds.length });
      axios.post(`${API}/production/label-check-log`, { found: found.map((f) => f.labelId), missing }).catch(() => {});

      if (found.length > 0) toast.success(`✅ ${found.length} orders auto-selected from label!`);
      if (missing.length > 0) toast.warning(`⚠️ ${missing.length} order IDs not found`);
      
    } catch (e) {
      console.error("Label upload error:", e);
      toast.error("Failed to read label PDF");
    } finally {
      setLabelUploading(false);
    }
  };
  const [selectedDrafts, setSelectedDrafts] = useState([]);

  const toggleDraftSelection = (draftId) => {
    setSelectedDrafts(prev => prev.includes(draftId) ? prev.filter(id => id !== draftId) : [...prev, draftId]);
  };

  const selectAllDrafts = () => {
    if (selectedDrafts.length === drafts.length) {
      setSelectedDrafts([]);
    } else {
      setSelectedDrafts(drafts.map(d => d.id));
    }
  };

  const bulkDeleteDrafts = async () => {
    try {
      await Promise.all(selectedDrafts.map(id => axios.delete(`${API}/production/drafts/${id}`)));
      setSelectedDrafts([]);
      fetchDrafts();
      toast.success(`Deleted ${selectedDrafts.length} draft(s)`);
    } catch (e) {
      toast.error("Failed to delete drafts");
    }
  };

  const fetchDrafts = async () => {
    try {
      const resp = await axios.get(`${API}/production/drafts`);
      setDrafts(resp.data.drafts || []);
    } catch (e) {}
  };

  const saveDraft = async (overrideName = null) => {
    if (selectedQueue.length === 0) { toast.error("No orders selected"); return; }
    const name = overrideName || draftName.trim() || `Draft ${new Date().toLocaleString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}`;
    setSavingDraft(true);
    try {
      const orderEdits = {};
      for (const orderId of selectedQueue) {
        const editsArray = [];
        const orderImageEdits = imageEdits[orderId] || {};
        for (const [imgPath, edit] of Object.entries(orderImageEdits)) {
          editsArray.push({ image_path: imgPath, ...edit });
        }
        if (editsArray.length > 0) orderEdits[orderId] = editsArray;
      }
      await axios.post(`${API}/production/drafts`, {
        name, order_ids: selectedQueue, order_edits: orderEdits, pdf_filename: pdfFileName
      });
      toast.success(`Draft "${name}" saved!`);
      setDraftName("");
      setShowDraftNameInput(false);
      fetchDrafts();
    } catch (e) {
      toast.error("Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const loadDraft = async (draft) => {
    setSelectedQueue(draft.order_ids);
    setPdfFileName(draft.pdf_filename || "");
    // Load edits
    const edits = {};
    for (const [orderId, editsArray] of Object.entries(draft.order_edits || {})) {
      edits[orderId] = {};
      for (const edit of editsArray) {
        const { image_path, ...editData } = edit;
        edits[orderId][image_path] = editData;
      }
    }
    setImageEdits(edits);
    setShowDrafts(false);
    setPdfHistoryOpen(false);
    
    // Auto-generate previews if draft was at previewed/edited stage
    if (draft.stage === "previewed" || draft.stage === "edited") {
      toast.success(`Draft "${draft.name}" loaded! Auto-generating previews...`);
      setTimeout(() => handleGenerateAllPreviews(), 500);
    } else {
      toast.success(`Draft "${draft.name}" loaded! Click Preview to continue.`);
    }
  };

  const deleteDraft = async (draftId) => {
    try {
      await axios.delete(`${API}/production/drafts/${draftId}`);
      fetchDrafts();
      toast.success("Draft deleted");
    } catch (e) {
      toast.error("Failed to delete draft");
    }
  };
  
  // Editor Modal State
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [originalImageSize, setOriginalImageSize] = useState({ width: 0, height: 0 });
  const imageRef = useRef(null);
  
  // Edit values
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(0);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 100, height: 100 });
  const [selectedRatio, setSelectedRatio] = useState(null);
  const [showCropBox, setShowCropBox] = useState(false); // Hide crop box by default
  const [useFullImage, setUseFullImage] = useState(false); // Full image with padding mode

  useEffect(() => {
    fetchReadyOrders();
    fetchDrafts();
  }, []);

  // Auto-save draft every 30 seconds if orders selected
  useEffect(() => {
    if (selectedQueue.length === 0) return;
    // Immediate save when queue changes
    const doAutoSave = async () => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      setAutoSaveStatus("saving");
      try {
        const orderEdits = {};
        for (const orderId of selectedQueue) {
          const editsArray = [];
          const orderImageEdits = imageEdits[orderId] || {};
          for (const [imgPath, edit] of Object.entries(orderImageEdits)) {
            editsArray.push({ image_path: imgPath, ...edit });
          }
          if (editsArray.length > 0) orderEdits[orderId] = editsArray;
        }
        const autoStage = allPreviewPages.length > 0 ? (Object.keys(imageEdits).length > 0 ? "edited" : "previewed") : "selected";
        const autoName = `Auto-save ${new Date().toLocaleString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}`;
        if (autoSaveDraftId) {
          try {
            // Update existing auto-save draft
            await axios.put(`${API}/production/drafts/${autoSaveDraftId}`, {
              name: autoName, order_ids: selectedQueue, order_edits: orderEdits, pdf_filename: pdfFileName, stage: autoStage
            });
          } catch (putErr) {
            // Draft was deleted - clear ID and create fresh draft
            setAutoSaveDraftId(null);
            localStorage.removeItem("autoSaveDraftId");
            const resp = await axios.post(`${API}/production/drafts`, {
              name: autoName, order_ids: selectedQueue, order_edits: orderEdits, pdf_filename: pdfFileName, stage: autoStage
            });
            setAutoSaveDraftId(resp.data.id);
            localStorage.setItem("autoSaveDraftId", resp.data.id);
          }
        } else {
          // Create new auto-save draft
          const resp = await axios.post(`${API}/production/drafts`, {
            name: autoName, order_ids: selectedQueue, order_edits: orderEdits, pdf_filename: pdfFileName, stage: autoStage
          });
          setAutoSaveDraftId(resp.data.id); localStorage.setItem("autoSaveDraftId", resp.data.id);
        }
        fetchDrafts();
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus(""), 2000);
      } catch (e) { 
        setAutoSaveStatus("error");
        setTimeout(() => setAutoSaveStatus(""), 3000);
      } finally {
        isSavingRef.current = false;
      }
    };
    doAutoSave();
    const interval = setInterval(doAutoSave, 30000);
    return () => clearInterval(interval);
  }, [selectedQueue, imageEdits, autoSaveDraftId]);

  const fetchReadyOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/production/ready-orders`);
      setReadyOrders(response.data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  // PDF History Functions
  const regeneratePdf = async (pdf) => {
    if (!pdf.processed_orders || pdf.processed_orders.length === 0) {
      toast.error("No order data available for regeneration");
      return;
    }
    if (!confirm(`Regenerate "${pdf.display_name}"? This will re-preview all ${pdf.order_count} orders and generate a new PDF.`)) return;
    
    toast.info("Starting regeneration in background...");
    
    toast.info(`Generating PDF with ${pdf.processed_orders.length} orders...`);
    
    // Generate new PDF with same orders using background job
    try {
      const startResp = await axios.post(`${API}/production/generate-pdf-background`, {
        order_ids: pdf.processed_orders,
        custom_filename: pdf.display_name.replace(/ /g, "_") + "_REGEN"
      });
      const jobId = startResp.data.job_id;
      toast.success("Regeneration started! Check PDF History in a few minutes.");
      
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const status = await axios.get(`${API}/production/pdf-job-status/${jobId}`);
          if (status.data.status === "completed") {
            clearInterval(poll);
            toast.success("PDF regenerated successfully!");
            fetchPdfHistory();
          } else if (status.data.status === "failed") {
            clearInterval(poll);
            toast.error("Regeneration failed");
          }
        } catch (e) { clearInterval(poll); }
      }, 3000);
    } catch (e) {
      toast.error("Failed to start regeneration");
    }
  };

  const fetchPdfHistory = async () => {
    try {
      setPdfLoading(true);
      const response = await axios.get(`${API}/production/pdf-history`);
      setPdfList(response.data.pdfs || []);
    } catch (error) {
      console.error("Error fetching PDF history:", error);
      toast.error("Failed to load PDF history");
    } finally {
      setPdfLoading(false);
    }
  };

  const syncToLedger = async () => {
    try {
      setSyncingLedger(true);
      const response = await axios.post(`${API}/production/sync-ledger`);
      const data = response.data;
      if (data.hasNewData) {
        toast.success(`Synced to Ledger - Batch ${data.syncLog?.batchNumber}, ${data.totalNewSheets} new sheets`);
      } else {
        toast.info("All accounts checked - no new production data found.");
      }
    } catch (error) {
      console.error("Ledger sync failed:", error);
      toast.error("Ledger sync failed - please try again");
    } finally {
      setSyncingLedger(false);
    }
  };

  const openPdfHistory = () => {
    setPdfHistoryOpen(true);
    fetchPdfHistory();
  };

  const copyShareLink = (pdf) => {
    const shareUrl = `${BACKEND_URL}${pdf.download_url}`;
    
    // Try to copy to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          setCopiedLink(pdf.id);
          toast.success("Link copied to clipboard!");
          setTimeout(() => setCopiedLink(null), 2000);
        })
        .catch(() => {
          // Fallback: show the link in a prompt
          prompt("Copy this link:", shareUrl);
          toast.info("Copy the link from the dialog");
        });
    } else {
      // Fallback for browsers without clipboard API
      prompt("Copy this link:", shareUrl);
      toast.info("Copy the link from the dialog");
    }
  };

  const downloadPdf = (pdf) => {
    window.open(`${BACKEND_URL}${pdf.download_url}`, '_blank');
  };

  const openPdfPreview = (pdf) => {
    setPreviewPdf(pdf);
    setPreviewDialogOpen(true);
  };

  const openRenameDialog = (pdf) => {
    setRenamingPdf(pdf);
    setNewPdfName(pdf.filename.replace(".pdf", ""));
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    if (!renamingPdf || !newPdfName.trim()) return;
    
    try {
      await axios.put(`${API}/production/pdf-history/${renamingPdf.filename}/rename`, {
        new_name: newPdfName
      });
      toast.success("PDF renamed successfully");
      setRenameDialogOpen(false);
      setRenamingPdf(null);
      fetchPdfHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rename PDF");
    }
  };

  const deletePdf = async (pdf) => {
    if (!pdf.is_synced) {
      const confirmed = window.confirm(
        `⚠️ "${pdf.display_name}" has NOT been synced to ledger yet!\n\nIf you delete it, the sheets won't be counted.\n\nAre you sure you want to delete it?`
      );
      if (!confirmed) return;
    } else {
      if (!confirm(`Delete "${pdf.display_name}"?`)) return;
    }
    
    try {
      await axios.delete(`${API}/production/pdf-history/${pdf.filename}`);
      toast.success("PDF deleted");
      fetchPdfHistory();
    } catch (error) {
      toast.error("Failed to delete PDF");
    }
  };

  const togglePdfSelection = (pdfId) => {
    setSelectedPdfs(prev => 
      prev.includes(pdfId) 
        ? prev.filter(id => id !== pdfId)
        : [...prev, pdfId]
    );
  };

  const selectAllPdfs = () => {
    if (selectedPdfs.length === pdfList.length) {
      setSelectedPdfs([]);
    } else {
      setSelectedPdfs(pdfList.map(p => p.id));
    }
  };

  const bulkDeletePdfs = async () => {
    if (selectedPdfs.length === 0) return;
    if (!confirm(`Delete ${selectedPdfs.length} selected PDF(s)?`)) return;
    
    try {
      await axios.post(`${API}/production/pdf-history/bulk-delete`, {
        filenames: selectedPdfs
      });
      toast.success(`Deleted ${selectedPdfs.length} PDF(s)`);
      setSelectedPdfs([]);
      fetchPdfHistory();
    } catch (error) {
      toast.error("Failed to delete PDFs");
    }
  };

  const filteredOrders = readyOrders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.order_id?.toLowerCase().includes(query) ||
      order.phone_number?.toLowerCase().includes(query) ||
      order.product_details?.toLowerCase().includes(query)
    );
  });

  const addToQueue = (order) => {
    if (selectedQueue.includes(order.id)) {
      toast.error("Order already in queue");
      return;
    }
    setSelectedQueue(prev => [...prev, order.id]);
    toast.success(`Added: ${order.order_id || order.phone_number}`);
  };

  const toggleQueue = (order) => {
    if (selectedQueue.includes(order.id)) {
      // Remove from queue
      removeFromQueue(order.id);
      toast.info(`Removed: ${order.order_id || order.phone_number}`);
    } else {
      // Add to queue
      setSelectedQueue(prev => [...prev, order.id]);
      toast.success(`Added: ${order.order_id || order.phone_number}`);
    }
  };

  const removeFromQueue = (orderId) => {
    setSelectedQueue(prev => prev.filter(id => id !== orderId));
    setQueuePreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[orderId];
      return newPreviews;
    });
    const newPreviews = { ...queuePreviews };
    delete newPreviews[orderId];
    updateAllPreviewPages(newPreviews, selectedQueue.filter(id => id !== orderId));
  };

  const clearQueue = () => {
    setSelectedQueue([]);
    setQueuePreviews({});
    setAllPreviewPages([]);
    setImageEdits({});
    setEditHistory([]);
    setPdfFileName("");
  };

  const getOrderById = (orderId) => readyOrders.find(o => o.id === orderId);

  const updateAllPreviewPages = (previews = queuePreviews, queue = selectedQueue) => {
    const pages = [];
    queue.forEach(orderId => {
      const preview = previews[orderId];
      if (preview?.preview_pages) {
        preview.preview_pages.forEach((page, pageIndex) => {
          pages.push({
            ...page,
            orderId,
            orderInfo: getOrderById(orderId),
            pageIndexInOrder: pageIndex,
            totalPagesInOrder: preview.preview_pages.length,
            imageFiles: preview.image_files || [],
            template: preview.template,
            totalImages: preview.total_images
          });
        });
      }
    });
    setAllPreviewPages(pages);
  };

  const handleRefreshEdits = async () => {
    // Check if there are any edits to refresh
    const hasEdits = Object.keys(editedIndices).some(orderId => editedIndices[orderId]?.length > 0);
    if (!hasEdits) {
      toast.info("No edits to refresh. Make some edits first!");
      return;
    }
    setRefreshingEdits(true);
    try {
      for (const orderId of selectedQueue) {
        const indices = editedIndices[orderId] || [];
        if (indices.length === 0) continue;
        const edits = imageEdits[orderId] || {};
        const editParams = Object.entries(edits).map(([index, edit]) => ({
          image_index: parseInt(index),
          ...edit
        }));
        const response = await axios.post(`${API}/production/update-edited-frames`, {
          order_id: orderId,
          edited_indices: indices,
          image_edits: editParams
        });
        if (response.data.success && response.data.updated_frames) {
          // Directly update the preview frames without regenerating entire preview
          setQueuePreviews(prev => {
            const orderPreview = prev[orderId];
            if (!orderPreview || !orderPreview.preview_pages) return prev;
            
            const framesPerPage = response.data.frames_per_page || orderPreview.frames_per_page || 18;
            const updatedPages = [...orderPreview.preview_pages];
            
            // For each updated frame, we need to update the corresponding page
            response.data.updated_frames.forEach(frame => {
              const pageIndex = frame.page_number - 1;
              if (pageIndex >= 0 && pageIndex < updatedPages.length) {
                // Mark this page as having updated frames
                if (!updatedPages[pageIndex].updated_frames) {
                  updatedPages[pageIndex].updated_frames = {};
                }
                updatedPages[pageIndex].updated_frames[frame.position_on_page] = frame.frame_data;
              }
            });
            
            return {
              ...prev,
              [orderId]: {
                ...orderPreview,
                preview_pages: updatedPages
              }
            };
          });
          toast.success(`Refreshed ${response.data.updated_count} edited frame(s) for order`);
        }
      }
      // Clear edited indices after refresh
      setEditedIndices({});
      toast.success("All edits applied successfully!");
      // DO NOT call handleGenerateAllPreviews - frames are already updated!
    } catch (error) {
      console.error("Refresh edits error:", error);
      toast.error("Failed to refresh edits");
    } finally {
      setRefreshingEdits(false);
    }
  };

  const handleGenerateAllPreviews = async () => {
    if (selectedQueue.length === 0) {
      toast.error("Please add orders to queue first");
      return;
    }

    setGeneratingPreview(true);
    const newPreviews = {};
    let successCount = 0;

    for (const orderId of selectedQueue) {
      try {
        const edits = imageEdits[orderId] || {};
        const editsArray = Object.entries(edits).map(([index, edit]) => ({
          image_index: parseInt(index),
          ...edit
        }));

        const response = await axios.post(`${API}/production/generate-preview`, {
          order_id: orderId,
          image_edits: editsArray.length > 0 ? editsArray : null
        }, { timeout: 120000 });
        
        newPreviews[orderId] = response.data;
        successCount++;
        await new Promise(r => setTimeout(r, 500));
      } catch (error) {
        console.error(`Error generating preview for ${orderId}:`, error);
      }
    }

    setQueuePreviews(newPreviews);
    updateAllPreviewPages(newPreviews, selectedQueue);
    setGeneratingPreview(false);
    if (successCount > 0) toast.success(`Generated ${successCount} preview(s)`);
  };

  const handleGenerateCombinedPDF = async () => {
    if (selectedQueue.length === 0) {
      toast.error("Please add orders to queue first");
      return;
    }
    setGeneratingPDF(true);
    setPdfProgress(0);
    
    try {
      const orderEdits = {};
      selectedQueue.forEach(orderId => {
        const edits = imageEdits[orderId] || {};
        const editsArray = Object.entries(edits).map(([index, edit]) => ({
          image_index: parseInt(index),
          ...edit
        }));
        if (editsArray.length > 0) {
          orderEdits[orderId] = editsArray;
        }
      });
      
      // Start background PDF generation
      const startResponse = await axios.post(`${API}/production/generate-pdf-background`, {
        order_ids: selectedQueue,
        order_edits: orderEdits,
        custom_filename: pdfFileName.trim() || null,
        courier_map: Object.keys(labelCourierMapRef.current).length > 0 ? labelCourierMapRef.current : null
      });
      
      const jobId = startResponse.data.job_id;
      toast.info(`Generating PDF for ${selectedQueue.length} orders...`);
      
      // Poll for job status
      let jobCompleted = false;
      
      const checkJobStatus = async () => {
        if (jobCompleted) return;
        
        try {
          const statusResponse = await axios.get(`${API}/production/pdf-job/${jobId}`);
          const { status, progress, result, error } = statusResponse.data;
          
          if (jobCompleted) return;
          
          if (status === "processing") {
            setPdfProgress(progress || 0);
            setTimeout(checkJobStatus, 2000);
          } else if (status === "completed") {
            jobCompleted = true;
            setPdfProgress(100);
            toast.success(`PDF generated! ${result.total_pages} pages - Check PDF History`);
            if (result.skipped_orders && result.skipped_orders.length > 0) {
              const skippedList = result.skipped_orders.map(o => o.order_id).join(", ");
              toast.warning(`⚠️ ${result.skipped_orders.length} order(s) skipped (missing pages) - Re-preview: ${skippedList}`, {duration: 10000});
            }
            fetchReadyOrders();
            clearQueue();
            setPdfHistoryOpen(true);
            fetchPdfHistory();
            setGeneratingPDF(false);
            setPdfProgress(0);
          } else if (status === "failed") {
            jobCompleted = true;
            toast.error(`PDF generation failed: ${error || "Unknown error"}`);
            setGeneratingPDF(false);
            setPdfProgress(0);
          }
        } catch (pollError) {
          if (jobCompleted) return;
          jobCompleted = true;
          console.error("Error polling job status:", pollError);
          toast.error("Lost connection to PDF job");
          setGeneratingPDF(false);
          setPdfProgress(0);
        }
      };
      
      setTimeout(checkJobStatus, 1000);
      
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Failed to start PDF generation");
      setGeneratingPDF(false);
      setPdfProgress(0);
    }
  };

    const zoomIn = () => setZoomLevel(prev => Math.min(prev + 25, 200));
  const zoomOut = () => setZoomLevel(prev => Math.max(prev - 25, 50));

  const handlePreviewClick = (e, page) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const cols = page.template?.columns || 6;
    const rows = page.template?.rows || 3;
    const cellWidth = rect.width / cols;
    const cellHeight = rect.height / rows;
    const col = Math.floor(x / cellWidth);
    const row = Math.floor(y / cellHeight);
    
    const framesPerPage = cols * rows;
    const startIdx = page.pageIndexInOrder * framesPerPage;
    const clickedIdx = startIdx + (row * cols) + col;
    
    if (clickedIdx < page.totalImages && clickedIdx < page.imageFiles.length) {
      openImageEditor(page, clickedIdx, page.imageFiles[clickedIdx]);
    }
  };

  const openImageEditor = async (page, imageIndex, filename) => {
    const existingEdits = imageEdits[page.orderId]?.[imageIndex] || {};
    setEditingImage({
      orderId: page.orderId,
      imageIndex,
      filename,
      orderInfo: page.orderInfo
    });
    
    setRotation(existingEdits.rotation || 0);
    setBrightness(existingEdits.brightness ? (existingEdits.brightness - 1) * 100 : 0);
    setCropArea({
      x: existingEdits.crop_x || 0,
      y: existingEdits.crop_y || 0,
      width: existingEdits.crop_width || 100,
      height: existingEdits.crop_height || 100
    });
    setSelectedRatio(null);
    setShowCropBox(false); // Hide crop box by default
    setUseFullImage(existingEdits.use_full_image || false); // Restore full image mode
    
    try {
      const response = await axios.get(`${API}/production/image/${page.orderId}/${imageIndex}`);
      setOriginalImageUrl(response.data.image_url);
      
      // Get original image dimensions
      const img = new Image();
      img.onload = () => {
        setOriginalImageSize({ width: img.width, height: img.height });
      };
      img.src = response.data.image_url;
    } catch (error) {
      setOriginalImageUrl(null);
    }
    
    setEditorOpen(true);
  };

  const applyRatio = (ratio) => {
    setSelectedRatio(ratio);
    setShowCropBox(true); // Show crop box when applying ratio
    setUseFullImage(false); // Disable full image mode
    if (ratio === null) return; // Free
    if (ratio === "original") {
      setCropArea({ x: 0, y: 0, width: 100, height: 100 });
      return;
    }
    
    // Calculate crop area based on ratio
    if (ratio > 1) {
      const height = 100 / ratio;
      setCropArea({ x: 0, y: (100 - height) / 2, width: 100, height });
    } else if (ratio < 1) {
      const width = 100 * ratio;
      setCropArea({ x: (100 - width) / 2, y: 0, width, height: 100 });
    } else {
      setCropArea({ x: 0, y: 0, width: 100, height: 100 });
    }
  };

  const applyPolaroidArea = () => {
    // Polaroid frame is 3x4 inches (width x height)
    const frameRatio = 3 / 4; // 0.75
    
    setShowCropBox(true); // Show crop box
    setUseFullImage(false); // Disable full image mode
    
    if (originalImageSize.width && originalImageSize.height) {
      const imgW = originalImageSize.width;
      const imgH = originalImageSize.height;
      const imageRatio = imgW / imgH;
      
      let cropX, cropY, cropWidth, cropHeight;
      
      if (imageRatio >= frameRatio) {
        // Image is WIDER - match HEIGHT, adjust WIDTH
        cropHeight = 100;
        cropWidth = (frameRatio / imageRatio) * 100;
        cropX = (100 - cropWidth) / 2;
        cropY = 0;
      } else {
        // Image is TALLER - match WIDTH, adjust HEIGHT
        cropWidth = 100;
        cropHeight = (imageRatio / frameRatio) * 100;
        cropX = 0;
        cropY = (100 - cropHeight) / 2;
      }
      
      setCropArea({ x: cropX, y: cropY, width: cropWidth, height: cropHeight });
      setSelectedRatio(frameRatio);
      toast.success(`📸 Polaroid 3×4 applied`);
    } else {
      setCropArea({ x: 0, y: 12.5, width: 100, height: 75 });
      setSelectedRatio(frameRatio);
      toast.success("📸 Polaroid 3×4 applied");
    }
  };

  const applyFullImage = () => {
    // Use full image with padding - no cropping at all
    setUseFullImage(true);
    setShowCropBox(false);
    setCropArea({ x: 0, y: 0, width: 100, height: 100 });
    setSelectedRatio(null);
    toast.success("📷 Full image mode - will fit with padding");
  };

  const enableCropMode = () => {
    setShowCropBox(true);
    setUseFullImage(false);
  };

  const rotateBy = (deg) => setRotation(prev => (prev + deg + 360) % 360);
  const resetEdits = () => {
    setRotation(0);
    setBrightness(0);
    setCropArea({ x: 0, y: 0, width: 100, height: 100 });
    setSelectedRatio(null);
    setShowCropBox(false);
    setUseFullImage(false);
  };

  const saveImageEdits = async () => {
    if (!editingImage) return;
    
    const { orderId, imageIndex } = editingImage;
    
    setEditHistory(prev => [...prev, { orderId, imageIndex, edits: imageEdits[orderId]?.[imageIndex] || null }]);
    
    setImageEdits(prev => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [imageIndex]: { 
          rotation,
          brightness: 1 + brightness / 100,
          contrast: 1.0,
          crop_x: cropArea.x,
          crop_y: cropArea.y,
          crop_width: cropArea.width,
          crop_height: cropArea.height,
          use_full_image: useFullImage // Save full image mode flag
        }
      }
    }));
    
    setEditorOpen(false);
    toast.success("Edit saved! Click Refresh Edits to see changes.", { duration: 2000 });
    // Track this image as edited
    if (editingImage?.orderId) {
      setEditedIndices(prev => ({
        ...prev,
        [editingImage.orderId]: [...(prev[editingImage.orderId] || []), editingImage.imageIndex].filter((v, i, a) => a.indexOf(v) === i)
      }));
    }
  };

  const undoLastEdit = () => {
    if (editHistory.length === 0) {
      toast.error("Nothing to undo");
      return;
    }
    
    const lastEdit = editHistory[editHistory.length - 1];
    setEditHistory(prev => prev.slice(0, -1));
    
    setImageEdits(prev => {
      const newEdits = { ...prev };
      if (lastEdit.edits === null) {
        if (newEdits[lastEdit.orderId]) {
          delete newEdits[lastEdit.orderId][lastEdit.imageIndex];
          if (Object.keys(newEdits[lastEdit.orderId]).length === 0) {
            delete newEdits[lastEdit.orderId];
          }
        }
      } else {
        newEdits[lastEdit.orderId] = {
          ...newEdits[lastEdit.orderId],
          [lastEdit.imageIndex]: lastEdit.edits
        };
      }
      return newEdits;
    });
    
    toast.success("Edit undone");
    setTimeout(() => handleGenerateAllPreviews(), 100);
  };

  const totalEdits = Object.values(imageEdits).reduce((sum, orderEdits) => 
    sum + Object.keys(orderEdits || {}).length, 0
  );

  return (
    <div className="flex h-screen overflow-hidden" data-testid="production-page">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header px-4 py-3 flex items-center justify-between border-b">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setShowPanel(!showPanel)} className="h-8 w-8">
              {showPanel ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
            </Button>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Printer className="h-5 w-5 text-primary" />
              Production
            </h1>
          </div>

          {/* Courier Summary Bar - Center */}
          {Object.keys(courierCounts).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {Object.entries(courierCounts).map(([courier, rawCounts]) => {
                const cData = typeof rawCounts === 'object' ? rawCounts : {ready: rawCounts, notReady: 0};
                const total = (cData.ready || 0) + (cData.notReady || 0);
                const bg = courier.startsWith('Delhivery') ? '#fee2e2' :
                           courier.startsWith('Xpressbees') ? '#ffedd5' :
                           courier.startsWith('Shadowfax') ? '#ede9fe' :
                           courier.startsWith('Valmo') ? '#dbeafe' :
                           courier.startsWith('ElasticRun') ? '#dcfce7' :
                           courier.startsWith('BlueDart') ? '#e0f2fe' : '#f3f4f6';
                const color = courier.startsWith('Delhivery') ? '#b91c1c' :
                              courier.startsWith('Xpressbees') ? '#c2410c' :
                              courier.startsWith('Shadowfax') ? '#7c3aed' :
                              courier.startsWith('Valmo') ? '#1d4ed8' :
                              courier.startsWith('ElasticRun') ? '#15803d' :
                              courier.startsWith('BlueDart') ? '#0369a1' : '#374151';
                return (
                  <div key={courier} style={{background: bg, borderRadius:'8px', padding:'3px 8px', fontSize:'11px', display:'flex', alignItems:'center', gap:'4px'}}>
                    <span style={{fontWeight:700, color}}>{courier}</span>
                    <span style={{color:'#6b7280'}}>|</span>
                    <span style={{color:'#15803d'}}>✓ {cData.ready||0}</span>
                    <span style={{color:'#6b7280'}}>|</span>
                    <span style={{color:'#dc2626'}}>✗ {cData.notReady||0}</span>
                    <span style={{color:'#6b7280'}}>| Total: {total}</span>
                  </div>
                );
              })}
              <button
                onClick={() => {
                  const text = Object.entries(courierCounts).map(([courier, rawC]) => {
                    const c = typeof rawC === 'object' ? rawC : {ready: rawC, notReady: 0};
                    const total = (c.ready||0) + (c.notReady||0);
                    const counts = c;
                    return `${courier}: Ready=${counts.ready||0}, Not Ready=${counts.notReady||0}, Total=${total}`;
                  }).join('\n');
                  navigator.clipboard.writeText(text);
                  toast.success('Copied!');
                }}
                style={{background:'transparent', border:'none', cursor:'pointer', padding:'2px 4px', color:'#6b7280'}}
                title="Copy courier summary"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          
          <div className="flex items-center gap-2">

            {allPreviewPages.length > 0 && (
              <div className="flex items-center gap-1 bg-muted rounded-lg px-2 py-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={zoomOut} disabled={zoomLevel <= 50}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium w-12 text-center">{zoomLevel}%</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={zoomIn} disabled={zoomLevel >= 200}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            )}
            {editHistory.length > 0 && (
              <Button variant="outline" size="sm" onClick={undoLastEdit}>
                <Undo2 className="h-4 w-4 mr-1" />
                Undo
              </Button>
            )}
            {totalEdits > 0 && <Badge variant="secondary">{totalEdits} edit(s)</Badge>}
            
            {/* PDF History Button */}
            <Button variant="outline" size="sm" onClick={openPdfHistory}>
              <History className="h-4 w-4 mr-1" />
              PDF History
            </Button>
            
            <Button variant="outline" size="sm" onClick={async () => {
              await fetchReadyOrders();
              // Re-check label if results exist
              if (labelResults) {
                const freshResp = await axios.get(`${API}/production/ready-orders`);
                const freshOrders = freshResp.data.orders || [];
                const found = [];
                const missing = [];
                const seqIds = labelSequence.length > 0 ? labelSequence : [...labelResults.found.map(f => f.labelId), ...labelResults.missing];
                const newQueue = [];
                for (const labelId of seqIds) {
                  const order = freshOrders.find(o => o.order_id && (o.order_id === labelId || o.order_id.includes(labelId)));
                  if (order) {
                    found.push({ labelId, orderId: order.id, order });
                    if (!newQueue.includes(order.id)) newQueue.push(order.id);
                  } else {
                    missing.push(labelId);
                  }
                }
                selectedQueue.forEach(id => { if (!newQueue.includes(id)) newQueue.push(id); });
                setSelectedQueue(newQueue);
                setLabelResults({ found, missing, total: seqIds.length });
                
                // Recalculate courier counts based on updated found/missing
                const updatedCourierCounts = {};
                for (const { labelId } of found) {
                  const courier = labelCourierMapRef.current[labelId] || "Unknown";
                  if (!updatedCourierCounts[courier]) updatedCourierCounts[courier] = { ready: 0, notReady: 0 };
                  updatedCourierCounts[courier].ready += 1;
                }
                for (const labelId of missing) {
                  const courier = labelCourierMapRef.current[labelId] || "Unknown";
                  if (!updatedCourierCounts[courier]) updatedCourierCounts[courier] = { ready: 0, notReady: 0 };
                  updatedCourierCounts[courier].notReady += 1;
                }
                setCourierCounts(updatedCourierCounts);
                
                toast.success(`Refreshed! ${found.length} found, ${missing.length} missing`);
              }
            }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel */}
          {showPanel && (
            <div className="w-72 border-r flex flex-col bg-muted/30">
              <div className="p-3 border-b">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Ready Orders</span>
                  <Badge variant="outline">{readyOrders.length}</Badge>
                </div>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input placeholder="Search..." className="pl-8 h-8 text-sm" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>
              </div>

              {/* Label PDF Upload - just after search */}
              <div className="px-3 pb-2 border-b">
                <input ref={labelInputRef} type="file" accept=".pdf" className="hidden" onChange={e => handleLabelUpload(e.target.files[0])} />
                <div
                  className="w-full border border-dashed border-blue-300 bg-blue-50 rounded-lg text-center cursor-pointer hover:bg-blue-100 transition-colors"
                  style={{padding: '6px'}}
                  onClick={() => !labelUploading && labelInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.background='#bfdbfe'; }}
                  onDragLeave={e => { e.currentTarget.style.background=''; }}
                  onDrop={e => {
                    e.preventDefault();
                    e.currentTarget.style.background='';
                    const file = e.dataTransfer.files[0];
                    if (file && file.type === 'application/pdf') handleLabelUpload(file);
                    else toast.error("Please drop a PDF file");
                  }}
                >
                  {labelUploading ? (
                    <span className="text-xs text-blue-600 flex items-center justify-center gap-1">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />Reading...
                    </span>
                  ) : (
                    <span className="text-xs text-blue-600 flex items-center justify-center gap-1">
                      <Upload className="h-3.5 w-3.5" />Upload or Drop Label PDF
                    </span>
                  )}
                </div>

                {/* Label Results */}
                {labelResults && (
                  <div className="mt-2 space-y-1.5">



                    {labelResults.relatedOrders?.length > 0 && (
                      <div className="bg-blue-50 border border-blue-300 rounded p-1.5">
                        <p className="text-[10px] font-semibold text-blue-700">🔗 Related ({labelResults.relatedOrders.length})</p>
                        {labelResults.relatedOrders.map(o => (
                          <div key={o.id} className="flex items-center justify-between">
                            <p className="text-[10px] text-blue-600 font-mono">{o.order_id}</p>
                            <Button size="sm" className="h-4 text-[9px] px-1 bg-blue-600" onClick={() => {
                              setSelectedQueue(prev => [...prev, o.id]);
                              setLabelResults(prev => ({...prev, relatedOrders: prev.relatedOrders.filter(r => r.id !== o.id)}));
                            }}>Add</Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <ScrollArea className="flex-1">
                {loading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : filteredOrders.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">No orders ready</div>
                ) : (
                  <div className="p-2 space-y-1.5">
                    {filteredOrders.map((order) => {
                      const isInQueue = selectedQueue.includes(order.id);
                      const hasMismatch = checkImageCountMismatch(order.total_image_count, order.product_details);
                      return (
                        <div
                          key={order.id}
                          className={`flex flex-col gap-2 p-2.5 rounded-lg cursor-pointer transition-colors ${
                            isInQueue 
                              ? "bg-emerald-50 border-2 border-emerald-400 hover:border-emerald-500" 
                              : hasMismatch
                                ? "bg-red-50 border-2 border-red-300 hover:border-red-400"
                                : "bg-slate-50 border border-slate-200 hover:border-primary/50"
                          }`}
                          onClick={() => toggleQueue(order)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm font-semibold text-slate-800">
                              {order.order_id || order.phone_number}
                            </span>
                            {isInQueue ? (
                              <Badge className="bg-emerald-600 text-white text-xs">#{selectedQueue.indexOf(order.id) + 1}</Badge>
                            ) : hasMismatch ? (
                              <Badge className="bg-red-500 text-white text-xs">!</Badge>
                            ) : (
                              <Plus className="h-4 w-4 text-slate-400" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${
                              hasMismatch ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                            }`}>
                              <ImageIcon className="h-3 w-3" />
                              <span className="text-xs font-semibold">{order.total_image_count}</span>
                            </div>
                            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${
                              hasMismatch ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                            }`}>
                              <FileText className="h-3 w-3" />
                              <span className="text-xs font-semibold">{order.product_details || "-"}</span>
                            </div>
                          </div>
                          {order.remarks && (
                            <div className="mt-1 px-1">
                              <span className="text-xs text-slate-500 italic">{order.remarks}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>

              {/* Queue Actions */}
              <div className="p-3 border-t bg-background flex flex-col" style={{maxHeight: '55vh', overflowY: 'auto'}}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold flex items-center gap-2">
                    <ListOrdered className="h-4 w-4 text-primary" />
                    Queue
                    {selectedQueue.length > 0 && (
                      <Badge variant="default" className="h-5 px-1.5">{selectedQueue.length}</Badge>
                    )}
                  </span>
                  {selectedQueue.length > 0 && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50" onClick={clearQueue}>
                      <Trash2 className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
                




                {/* Not Found Orders */}
                {labelResults?.missing?.length > 0 && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-2 mb-2">
                    <p className="text-xs font-semibold text-red-700 mb-1">❌ Not Found ({labelResults.missing.length})</p>
                    <div className="max-h-20 overflow-y-auto space-y-0.5">
                      {labelResults.missing.map(id => (
                        <p key={id} className="text-[10px] text-red-600 font-mono">{id}</p>
                      ))}
                    </div>
                  </div>
                )}

                {selectedQueue.length === 0 ? (
                  <div className="text-center py-4 border-2 border-dashed rounded-lg">
                    <p className="text-sm text-muted-foreground">Click orders to add to queue</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1.5 mb-2 max-h-48 overflow-y-auto pr-1">
                      {(() => {
                        const boundaries = {};
                        let lastCourier = null;
                        let lastIdx = -1;
                        selectedQueue.forEach((oid, i) => {
                          const ord = getOrderById(oid);
                          const lid = ord?.order_id;
                          const c = lid ? (labelCourierMapRef.current[lid] || null) : null;
                          if (c && c !== 'Unknown') {
                            if (c !== lastCourier) {
                              boundaries[i] = { type: 'first', courier: c };
                              if (lastCourier && lastIdx >= 0) {
                                boundaries[lastIdx] = boundaries[lastIdx]?.type === 'first'
                                  ? { type: 'both', courier: lastCourier }
                                  : { type: 'last', courier: lastCourier };
                              }
                              lastCourier = c;
                            }
                            lastIdx = i;
                          }
                        });
                        if (lastCourier && lastIdx >= 0) {
                          boundaries[lastIdx] = boundaries[lastIdx]?.type === 'first'
                            ? { type: 'both', courier: lastCourier }
                            : { type: 'last', courier: lastCourier };
                        }
                        window._courierBoundaries = boundaries;
                        return null;
                      })()}
                      {selectedQueue.map((orderId, index) => {
                        const courierInfo = window._courierBoundaries ? window._courierBoundaries[index] : null;
                        const order = getOrderById(orderId);
                        const hasMismatch = order ? checkImageCountMismatch(order.total_image_count, order.product_details) : false;
                        return (
                          <div 
                            key={orderId} 
                            className={`flex items-center gap-2 rounded-lg p-2.5 transition-colors border-2 ${
                              hasMismatch 
                                ? "bg-red-50 border-red-300" 
                                : "bg-emerald-50 border-emerald-300"
                            }`}
                          >
                            <Badge className={`h-6 w-6 p-0 flex items-center justify-center text-xs font-bold rounded-full shrink-0 ${hasMismatch ? "bg-red-500" : "bg-emerald-600"} text-white`}>
                              {index + 1}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <span className="font-mono text-[10px] font-semibold text-slate-800 break-all block">
                                  {order?.order_id || order?.phone_number || "No ID"}
                                </span>
                                {courierInfo && (
                                  <span style={{
                                    fontSize:'9px', fontWeight:700, padding:'1px 4px',
                                    borderRadius:'4px', background: courierColors[courierInfo.courier] || '#64748b',
                                    color:'white', whiteSpace:'nowrap', flexShrink:0
                                  }}>
                                    {courierInfo.type === 'first' ? '▶ ' : courierInfo.type === 'last' ? '◀ ' : '◀▶ '}
                                    {courierShortCodes[courierInfo.courier] || courierInfo.courier}
                                  </span>
                                )}
                              </div>
                              {order && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className={`text-[10px] px-1.5 rounded-full ${hasMismatch ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                                    📷 {order.total_image_count}
                                  </span>
                                  <span className={`text-[10px] px-1.5 rounded-full ${hasMismatch ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                                    {order.product_details || "-"}
                                  </span>
                                </div>
                              )}
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6 text-slate-400 hover:text-red-500 hover:bg-red-50 shrink-0" 
                              onClick={() => removeFromQueue(orderId)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="space-y-2">
                      <Button size="sm" className="w-full" onClick={handleGenerateAllPreviews} disabled={generatingPreview}>
                        {generatingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="h-4 w-4 mr-1" />Preview</>}
                      </Button>
                      {Object.keys(editedIndices).some(k => editedIndices[k]?.length > 0) && (
                        <Button size="sm" variant="outline" className="w-full border-orange-500 text-orange-600 hover:bg-orange-50" onClick={handleRefreshEdits} disabled={refreshingEdits}>
                          {refreshingEdits ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" />Refresh Edits ({Object.values(editedIndices).flat().length})</>}
                        </Button>
                      )}
                      {allPreviewPages.length > 0 && (
                        <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleGenerateCombinedPDF} disabled={generatingPDF}>
                          {generatingPDF ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Generating... {pdfProgress}%</> : <><Download className="h-4 w-4 mr-1" />Generate PDF</>}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Preview Area */}
          <div className="flex-1 overflow-hidden bg-neutral-100 flex flex-col">
            {allPreviewPages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Printer className="h-20 w-20 text-muted-foreground/20 mb-4" />
                <p className="text-muted-foreground text-lg">
                  {selectedQueue.length === 0 ? "Select orders and click Preview" : "Click Preview to generate designs"}
                </p>
              </div>
            ) : (
              <>
                {/* PDF Name & Info Header */}
                <div className="bg-white border-b px-6 py-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex items-center gap-2">
                      <FileDown className="h-5 w-5 text-primary" />
                      <Label htmlFor="pdfName" className="text-sm font-semibold whitespace-nowrap">PDF Name:</Label>
                    </div>
                    <div className="flex-1 max-w-md">
                      <Input
                        id="pdfName"
                        placeholder="Enter PDF name (also used as draft name)"
                        value={pdfFileName}
                        onChange={(e) => setPdfFileName(e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">.pdf</span>
                    {autoSaveStatus === "saving" && <span className="text-xs text-blue-500 animate-pulse">💾 Saving...</span>}
                    {autoSaveStatus === "saved" && <span className="text-xs text-green-500">✅ Auto-saved</span>}
                    {autoSaveStatus === "error" && <span className="text-xs text-red-500">❌ Save failed</span>}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => saveDraft(pdfFileName || `Draft ${new Date().toLocaleString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}`)}
                      disabled={savingDraft || selectedQueue.length === 0}
                    >
                      {savingDraft ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Saving...</> : <><Save className="h-4 w-4 mr-1" />Save Draft</>}
                    </Button>
                  </div>

                  {/*
                      )}
                    </div>
                  )}

                  
                  
                  {/* Page Count Summary */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm font-bold">{allPreviewPages.length} Page{allPreviewPages.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg">
                      <ListOrdered className="h-4 w-4" />
                      <span className="text-sm font-bold">{selectedQueue.length} Order{selectedQueue.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
                
                <ScrollArea className="flex-1">
                  <div className="p-6 space-y-8">
                    {selectedQueue.map(orderId => {
                      const preview = queuePreviews[orderId];
                      const order = getOrderById(orderId);
                      if (!preview) return null;
                      
                      return (
                        <div key={orderId} className="space-y-4">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="font-mono text-sm px-3 py-1">{order?.order_id || order?.phone_number}</Badge>
                            <span className="text-sm text-muted-foreground">{preview.total_images} images • {preview.total_pages} page(s)</span>
                          </div>
                          
                          <div className="space-y-6">
                            {preview.preview_pages?.map((page, pageIndex) => {
                              const pageData = allPreviewPages.find(p => p.orderId === orderId && p.pageIndexInOrder === pageIndex);
                              
                              return (
                                <div key={pageIndex} className="relative">
                                  <div className="absolute top-3 left-3 z-10">
                                    <Badge className="bg-black/70 text-white">Page {pageIndex + 1}</Badge>
                                  </div>
                                  
                                  <div 
                                    className="relative cursor-pointer group rounded-lg overflow-hidden shadow-xl border-4 border-white hover:border-primary transition-colors mx-auto"
                                    style={{ width: `${zoomLevel}%`, maxWidth: '100%' }}
                                    onClick={(e) => pageData && handlePreviewClick(e, pageData)}
                                  >
                                    <img src={page.image_data} alt={`Page ${pageIndex + 1}`} className="w-full h-auto" draggable={false} />
                                    <div className="absolute inset-0 grid" style={{gridTemplateColumns: `repeat(${pageData?.template?.columns || 6}, 1fr)`, gridTemplateRows: `repeat(${pageData?.template?.rows || 3}, 1fr)`}}>
                                      {Array.from({length: (pageData?.template?.columns || 6) * (pageData?.template?.rows || 3)}).map((_, i) => (
                                        <div key={i} className="border border-transparent hover:border-2 hover:border-primary hover:bg-primary/10 transition-all" />
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Image Editor Modal */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[95vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              Edit: {editingImage?.filename} — {editingImage?.orderInfo?.order_id || editingImage?.orderInfo?.phone_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-1 overflow-hidden">
            {/* Image Preview with Crop Box */}
            <div className="flex-1 bg-neutral-900 flex items-center justify-center p-4 relative overflow-hidden">
              {originalImageUrl ? (
                <div 
                  ref={imageRef}
                  className="relative"
                  style={{
                    transform: `rotate(${rotation}deg) scale(${rotation % 180 !== 0 ? 0.7 : 1})`,
                    filter: `brightness(${1 + brightness/100})`,
                    transition: 'transform 0.3s, filter 0.2s',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                >
                  <img
                    src={originalImageUrl}
                    alt="Preview"
                    className="max-w-[500px] max-h-[55vh] object-contain"
                    draggable={false}
                  />
                  {/* Only show crop box when crop mode is enabled */}
                  {showCropBox && (
                    <CropBox 
                      imageRef={imageRef}
                      cropArea={cropArea}
                      setCropArea={setCropArea}
                      aspectRatio={selectedRatio}
                    />
                  )}
                  {/* Show full image indicator */}
                  {useFullImage && (
                    <div className="absolute inset-0 border-4 border-green-500 border-dashed pointer-events-none flex items-center justify-center">
                      <div className="bg-green-500/90 text-white px-4 py-2 rounded-lg font-medium">
                        📷 Full Image Mode
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              )}
            </div>

            {/* Controls Panel */}
            <div className="w-64 bg-slate-800 text-white p-3 overflow-y-auto">
              {/* Crop Ratio */}
              <div className="mb-3">
                <Label className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1 mb-1.5">
                  <Crop className="h-3 w-3" />
                  Crop Ratio
                </Label>
                <div className="grid grid-cols-3 gap-1">
                  {CROP_RATIOS.map((ratio) => (
                    <Button
                      key={ratio.name}
                      variant={selectedRatio === ratio.value && showCropBox ? "default" : "secondary"}
                      size="sm"
                      className={`text-[10px] h-6 ${selectedRatio === ratio.value && showCropBox ? "bg-blue-600" : "bg-slate-700 hover:bg-slate-600"}`}
                      onClick={() => applyRatio(ratio.value)}
                    >
                      {ratio.name}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Quick Actions - Polaroid & Full Image */}
              <div className="grid grid-cols-2 gap-1.5 mb-3">
                <Button 
                  className="font-medium bg-amber-500 hover:bg-amber-600 text-black h-8 text-xs"
                  onClick={applyPolaroidArea}
                >
                  📸 Polaroid
                </Button>
                <Button 
                  className={`font-medium h-8 text-xs ${useFullImage ? "bg-green-600 hover:bg-green-700" : "bg-slate-700 hover:bg-slate-600"}`}
                  onClick={applyFullImage}
                >
                  📷 Full Image
                </Button>
              </div>

              {/* Brightness */}
              <div className="mb-3">
                <Label className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1 mb-1">
                  <Sun className="h-3 w-3" />
                  Brightness: {brightness}
                </Label>
                <Slider
                  value={[brightness]}
                  min={-50}
                  max={50}
                  step={1}
                  onValueChange={([v]) => setBrightness(v)}
                />
              </div>

              {/* Rotation */}
              <div className="mb-3">
                <Label className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1 mb-1.5">
                  <RotateCw className="h-3 w-3" />
                  Rotation
                </Label>
                <div className="grid grid-cols-4 gap-1">
                  <Button variant="secondary" size="sm" className="bg-slate-700 hover:bg-slate-600 h-7 text-[10px]" onClick={() => rotateBy(90)}>
                    90°
                  </Button>
                  <Button variant="secondary" size="sm" className="bg-slate-700 hover:bg-slate-600 h-7 text-[10px]" onClick={() => rotateBy(-90)}>
                    -90°
                  </Button>
                  <Button variant="secondary" size="sm" className="bg-slate-700 hover:bg-slate-600 h-7 text-[10px]" onClick={() => rotateBy(180)}>
                    180°
                  </Button>
                  <Button variant="secondary" size="sm" className="bg-slate-700 hover:bg-slate-600 h-7 text-[10px]" onClick={resetEdits}>
                    Reset
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-1.5 pt-2 border-t border-slate-700">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 h-9" onClick={saveImageEdits}>
                  <Save className="h-4 w-4 mr-2" />
                  Save & Apply
                </Button>
                <Button variant="outline" className="w-full border-slate-600 text-white hover:bg-slate-700 h-8 text-sm" onClick={() => setEditorOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF History Sheet */}
      <Sheet open={pdfHistoryOpen} onOpenChange={setPdfHistoryOpen}>
        <SheetContent className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              PDF Download History
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-4">
            {/* Tabs: PDF History / Drafts */}
            <div className="flex gap-2 mb-4 border-b pb-2">
              <button
                className={`text-sm px-3 py-1 rounded-t ${!showDrafts ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setShowDrafts(false)}
              >PDF History</button>
              <button
                className={`text-sm px-3 py-1 rounded-t ${showDrafts ? 'bg-purple-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => { setShowDrafts(true); fetchDrafts(); }}
              >📋 Drafts ({drafts.length})</button>
            </div>

            {/* Drafts List */}
            {showDrafts ? (
              <div className="space-y-3">

                {/* Bulk Actions */}
                {drafts.length > 0 && (
                  <div className="flex items-center justify-between py-1 border-b">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedDrafts.length === drafts.length && drafts.length > 0}
                        onCheckedChange={selectAllDrafts}
                      />
                      <span className="text-xs text-gray-500">
                        {selectedDrafts.length > 0 ? `${selectedDrafts.length} selected` : "Select all"}
                      </span>
                    </div>
                    {selectedDrafts.length > 0 && (
                      <Button variant="destructive" size="sm" onClick={bulkDeleteDrafts} className="h-7 text-xs">
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete ({selectedDrafts.length})
                      </Button>
                    )}
                  </div>
                )}
                {drafts.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No drafts saved yet</p>
                ) : (
                  drafts.map(draft => (
                    <div key={draft.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${selectedDrafts.includes(draft.id) ? 'bg-primary/5 border-primary/30' : 'bg-background hover:bg-muted/50'}`}>
                      <Checkbox
                        checked={selectedDrafts.includes(draft.id)}
                        onCheckedChange={() => toggleDraftSelection(draft.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{draft.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-[10px] h-4 px-1">{draft.order_ids.length} orders</Badge>
                          <span className="text-[10px] text-muted-foreground">{new Date(draft.updated_at).toLocaleString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}</span>
                          {draft.stage && <Badge variant="secondary" className="text-[10px] h-4 px-1">{draft.stage}</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => { loadDraft(draft); setPdfHistoryOpen(false); }} className="h-7 text-xs">
                          Load
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteDraft(draft.id)} className="h-7 w-7 p-0 text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : null}

            {/* PDF History - only show when not in drafts tab */}
            {!showDrafts && <>
            {/* Total Sheets Summary */}
            {pdfList.length > 0 && (
              <div className="flex items-center justify-between mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-xs font-semibold text-blue-700">
                  Total: {pdfList.reduce((sum, p) => sum + (p.page_count || 0), 0)} sheets ({pdfList.length} PDFs)
                  {pdfList.filter(p => !p.is_synced).length > 0 && (
                    <span className="ml-2 text-orange-600">⏳ {pdfList.filter(p => !p.is_synced).length} not synced</span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                <OutsourceSyncButton refreshKey={pdfList.length + "-" + pdfList.reduce((sum, p) => sum + (p.page_count || 0), 0)} />
                <button
                  onClick={() => {
                    const account = pdfList[0]?.display_name?.match(/^(TB|MC|YDL|WF)/)?.[0] || 'TB';
                  const text = account + ' ACCOUNT :\n' +
                    pdfList.map(p => {
                      const cleanName = p.display_name.replace(/\s*\d+\s*SHEETS?/i,'').trim();
                      return `${cleanName} : ${p.page_count} Sheets`;
                    }).join('\n') +
                    `\n\nTotal: ${pdfList.reduce((sum, p) => sum + (p.page_count || 0), 0)} Sheets`;
                    navigator.clipboard.writeText(text);
                    toast.success('Copied!');
                  }}
                  style={{background:'transparent', border:'none', cursor:'pointer', color:'#2563eb'}}
                  title="Copy sheet summary"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
              </div>
            )}
            {/* Bulk Actions */}
            {pdfList.length > 0 && (
              <div className="flex items-center justify-between mb-4 pb-4 border-b">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedPdfs.length === pdfList.length && pdfList.length > 0}
                    onCheckedChange={selectAllPdfs}
                  />
                  <span className="text-sm text-muted-foreground">
                    {selectedPdfs.length > 0 ? `${selectedPdfs.length} selected` : "Select all"}
                  </span>
                </div>
                {selectedPdfs.length > 0 && (
                  <Button variant="destructive" size="sm" onClick={bulkDeletePdfs}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete ({selectedPdfs.length})
                  </Button>
                )}
              </div>
            )}
            
            {/* PDF List */}
            <ScrollArea className="h-[calc(100vh-200px)]">
              {pdfLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : pdfList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">No PDFs generated yet</p>
                  <p className="text-sm text-muted-foreground/70">Generate PDFs from the production queue</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pdfList.map((pdf) => (
                    <div
                      key={pdf.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                        selectedPdfs.includes(pdf.id) ? "bg-primary/5 border-primary/30" : "hover:bg-muted/50"
                      }`}
                    >
                      <Checkbox
                        checked={selectedPdfs.includes(pdf.id)}
                        onCheckedChange={() => togglePdfSelection(pdf.id)}
                        className="mt-1"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-red-500 shrink-0" />
                          <span className="font-medium text-sm truncate">{pdf.display_name}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{pdf.order_count} order(s)</span>
                          <span className="text-blue-600 font-medium">{pdf.page_count} sheet(s)</span>
                          <span>{pdf.size_mb} MB</span>
                          {pdf.is_synced
                            ? <span className="text-green-600 text-xs">✅ Synced</span>
                            : <span className="text-orange-500 text-xs">⏳ Not Synced</span>
                          }
                          <span>{pdf.created_at ? new Date(pdf.created_at).toLocaleString('en-IN', {day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true}) : ''}</span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-1">
                        {/* Preview */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openPdfPreview(pdf)}
                          title="Preview PDF"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        {/* Share/Copy Link */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => copyShareLink(pdf)}
                          title="Copy share link"
                        >
                          {copiedLink === pdf.id ? (
                            <CheckCheck className="h-4 w-4 text-green-500" />
                          ) : (
                            <Share2 className="h-4 w-4" />
                          )}
                        </Button>
                        
                        {/* Download */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => downloadPdf(pdf)}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        
                        {/* More Actions Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openPdfPreview(pdf)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => regeneratePdf(pdf)}>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Regenerate
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openRenameDialog(pdf)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.open(`${BACKEND_URL}${pdf.download_url}`, '_blank')}>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Open in new tab
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => deletePdf(pdf)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            </> }
          </div>
        </SheetContent>
      </Sheet>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename PDF</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>New name</Label>
            <Input
              value={newPdfName}
              onChange={(e) => setNewPdfName(e.target.value)}
              placeholder="Enter new name"
              className="mt-2"
              onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            />
            <p className="text-xs text-muted-foreground mt-2">.pdf extension will be added automatically</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRename}>Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="sm:max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              {previewPdf?.display_name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {previewPdf && (
              <iframe
                src={`${BACKEND_URL}/api/production/preview-pdf/${previewPdf.filename}`}
                className="w-full h-full border-0"
                title="PDF Preview"
              />
            )}
          </div>
          <div className="px-6 py-3 border-t flex items-center justify-between bg-muted/30">
            <div className="text-sm text-muted-foreground">
              {previewPdf?.order_count} order(s) • {previewPdf?.page_count} sheet(s) • {previewPdf?.size_mb} MB
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => copyShareLink(previewPdf)}>
                <Share2 className="h-4 w-4 mr-2" />
                Share Link
              </Button>
              <Button onClick={() => downloadPdf(previewPdf)}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
